import PropTypes from 'prop-types';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// @mui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
// utils
import { fData } from 'src/utils/format-number';
import { fDateTime } from 'src/utils/format-time';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
import { useBackToClose } from 'src/hooks/use-back-to-close';
// components
import { fileFormat } from 'src/components/file-thumbnail';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
//
import { yupResolver } from '@hookform/resolvers/yup';
import { inputBaseClasses, MenuItem, Tooltip } from '@mui/material';
import { domAnimation, LazyMotion, m } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { ConfirmDialog } from 'src/components/custom-dialog';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import FormProvider, { RHFTextField, RHFUploadBox } from 'src/components/hook-form';
import Image from 'src/components/image';
import Label from 'src/components/label';
import Lightbox, { useLightBox } from 'src/components/lightbox';
import { useMockedUser } from 'src/hooks/use-mocked-user';
import * as Yup from 'yup';
import FileManagerShareDialog from './file-manager-share-dialog';
import QuickMoveModal from './quick-move-modal';
import { useSnackbar } from 'src/components/snackbar';
import {
  useLazyGetFilesQuery,
  useUpdateFileStatusApprovedMutation,
  useUpdateFileStatusRejectedMutation,
} from 'src/store/Reducer/file';
import { useDeleteFolderMutation, useRenameFolderMutation } from 'src/store/Reducer/folder';
import { LoadingScreen } from 'src/components/loading-screen';
export default function FileManagerFileDetails({
  item,
  open,
  onCopyLink,
  refetch,
  onClose,
  isLoading,
  onDelete,
  fromCategory = false,
  fromProject = false,
  hideSelectButton = false,
  combinedView = false,
  groupBy = 'category',
  title,
  ...other
}) {

  const [itemState, setItemState] = useState(item);

  const loading = useBoolean();

  useEffect(() => {
    if (item) setItemState(item);
  }, [item]);

  useEffect(() => {
    if (isLoading) {
      loading.onTrue();
    } else {
      setTimeout(() => {
        loading.onFalse();
      }, 1000);
    }
  }, [isLoading]);



  const formattedImages = useMemo(() => {
    if (!itemState?.data || !Array.isArray(itemState?.data)) return [];
    return itemState?.data?.slice()
      ?.sort((a, b) => Number(a?.file_status) - Number(b?.file_status))
      ?.map((f) => ({
        id: f?.category_dir_id,
        folder_id: f?.folder_id,
        category_id: f?.category_id,
        title: f?.file?.split('/')?.pop(),
        status: f?.file_status,
        src: f?.file,
        type: f?.file_type,
        category_title: f?.category_title,
        is_confidential: f?.is_confidential,
        folder_name: f?.folder_name,
        caption: f?.caption,
        location: f?.location,
        user_name: f?.user_name,
      }));
  }, [itemState]);

  const pendingImages = useMemo(
    () => formattedImages?.filter((f) => f?.status === '0'),
    [formattedImages]
  );
  const acceptedImages = useMemo(
    () => formattedImages?.filter((f) => f?.status === '1'),
    [formattedImages]
  );
  const rejectedImages = useMemo(
    () => formattedImages?.filter((f) => f?.status === '2'),
    [formattedImages]
  );

  const [updateFileStatusApproved] = useUpdateFileStatusApprovedMutation();

  const [renameFolder] = useRenameFolderMutation();

  const [deleteFolder] = useDeleteFolderMutation();

  const [updateFileStatusRejected] = useUpdateFileStatusRejectedMutation();

  const [triggerGetFiles] = useLazyGetFilesQuery();

  const { user } = useMockedUser();

  const toggleTags = useBoolean(true);

  const share = useBoolean();

  const confirmAccept = useBoolean();

  const confirmReject = useBoolean();

  const properties = useBoolean(true);

  const popover = usePopover();

  const edit = useBoolean();

  const deleteFile = useBoolean();

  const moveFile = useBoolean();


  const [inviteEmail, setInviteEmail] = useState('');

  const [selectedImages, setSelectedImages] = useState([]);

  const [showImageSelection, setShowImageSelection] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const defaultValues = useMemo(
    () => ({
      category: itemState?.data?.[0]?.category_title || '',
      folder: itemState?.data?.[0]?.folder_name || '',
      images: formattedImages || [],
    }),
    [itemState, formattedImages]
  );

  const schema = Yup.object().shape({
    category: Yup.string().required('Category is required'),
    folder: Yup.string().required('Folder is required'),
    images: Yup.array().min(1, 'Select at least one image'),
  });

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const { reset } = methods;


  const lightbox = useLightBox(methods.getValues('images'));

  // Browser / mouse Back closes this overlay instead of leaving the page.
  useBackToClose(open, () => {
    setShowImageSelection(false);
    setSelectedImages([]);
    edit.onFalse();
    onClose?.();
  });

  useEffect(() => {
    if (formattedImages && formattedImages.length > 0) {
      reset({
        category: itemState?.data?.[0]?.category_title || '',
        folder: itemState?.data?.[0]?.folder_name || '',
        images: formattedImages,
      });
    }
  }, [itemState, formattedImages, reset]);

  const handleDrop = useCallback(
    (acceptedFiles) => {
      const newSlides = acceptedFiles.map((file) => {
        const url = URL.createObjectURL(file);

        return {
          id: Math.random(),
          title: file.name,
          src: url,
          preview: url,
        };
      });

      const currentImages = methods.getValues('images');

      methods.setValue('images', [...currentImages, ...newSlides], { shouldValidate: true });
    },
    [methods.setValue, methods.getValues]
  );

  const handleChangeInvite = useCallback((event) => {
    setInviteEmail(event.target.value);
  }, []);

  const selectAll = () => {
    setSelectedImages((prev) =>
      prev.length === methods.watch('images').length ? [] : methods.watch('images').map((f) => f)
    );
  };

  const deSelect = () => {
    setSelectedImages([]);
    setShowImageSelection(false);
  };

  const select = useCallback(() => {
    const allImages = methods.getValues('images') || [];
    if (allImages?.length === 0) return;
    setShowImageSelection(true);
    if (selectedImages?.length != allImages?.length) {
      setSelectedImages([]);
    } else if (selectedImages?.length === allImages?.length) {
      setSelectedImages([]);
    } else {
      setSelectedImages(allImages);
    }
  }, [methods, selectedImages]);

  const handleSelectImage = (image) => {
    setSelectedImages((prevSelected) => {
      const isSelected = prevSelected?.some((img) => img?.id === image?.id);
      if (isSelected) {
        return prevSelected.filter((img) => img?.id !== image?.id);
      } else {
        return [...prevSelected, image];
      }
    });
  };
  const handleAccept = async () => {
    try {
      if (showImageSelection) {
        if (!selectedImages?.length) {
          enqueueSnackbar('Please select at least one image to accept.', { variant: 'warning' });
          return;
        }
        const selectedIds = selectedImages?.map((img) => img?.id);
        await updateFileStatusApproved({
          id: selectedImages[0]?.folder_id,
          data: { ids: selectedIds },
        }).unwrap();
        await triggerGetFiles({
          category: selectedImages[0]?.category_id,
          folder: selectedImages[0]?.folder_id,
        }).unwrap();
        setSelectedImages([]);
        setShowImageSelection(false);
        enqueueSnackbar('Images approved successfully!', { variant: 'success', anchorOrigin: { vertical: 'bottom', horizontal: 'right' } });
      } else {
        const allIds = itemState?.data?.map((img) => img?.category_dir_id);
        if (!allIds?.length) {
          enqueueSnackbar('No images found to approve.', { variant: 'warning' });
          return;
        }
        await updateFileStatusApproved({
          id: itemState?.data?.[0]?.folder_id,
          data: { ids: allIds },
        }).unwrap();
        await triggerGetFiles({
          category: itemState?.data?.[0]?.category_id,
          folder: itemState?.data?.[0]?.folder_id,
        }).unwrap();
        setSelectedImages([]);
        setShowImageSelection(false);
        enqueueSnackbar('Images approved successfully!', { variant: 'success', anchorOrigin: { vertical: 'bottom', horizontal: 'right' } });
      }
    } catch (error) {
      enqueueSnackbar(error?.data?.message || 'Failed to update approve status', {
        variant: 'error',
      });
    }
  };

  const handleReject = async () => {
    try {
      await updateFileStatusRejected({
        id: itemState?.data?.length && itemState?.data[0]?.folder_id,
      }).unwrap();
      await triggerGetFiles({
        category: itemState?.data[0]?.category_id,
        folder: itemState?.data[0]?.folder_id,
      }).unwrap();
      setSelectedImages([]);
      setShowImageSelection(false);
      enqueueSnackbar('Images rejected successfully!', { variant: 'success', anchorOrigin: { vertical: 'bottom', horizontal: 'right' } });
    } catch (error) {
      enqueueSnackbar(error?.data?.message || 'Failed to update reject status', {
        variant: 'error',
      });
    }
  };

  const handleRenameFolder = async () => {
    try {
      const folder_name = methods.getValues('folder');
      const data = { id: itemState?.data?.length && itemState?.data[0]?.folder_id, folder_name };
      await renameFolder(data).unwrap();
      await triggerGetFiles({
        category: itemState?.data[0]?.category_id,
        folder: itemState?.data[0]?.folder_id,
      }).unwrap();
      enqueueSnackbar('Folder renamed successfully!', { variant: 'success' });
      edit.onFalse();
    } catch (error) {
      enqueueSnackbar(error?.data?.message || 'Failed to rename folder', { variant: 'error' });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteFolder(itemState?.data?.length && itemState?.data[0]?.folder_id).unwrap();
      enqueueSnackbar('Folder deleted successfully!', { variant: 'success' });
      setShowImageSelection(false);
      setSelectedImages([]);
      edit.onFalse();
      deleteFile.onFalse();
      refetch();
      onClose();
    } catch (error) {
      enqueueSnackbar(error?.data?.message || 'Failed to delete folder', { variant: 'error' });
    }
  };

  const handleDownloadSelected = async () => {
    try {
      if (showImageSelection) {

        if (!selectedImages?.length) {
          enqueueSnackbar('Please select at least one image to download.', { variant: 'warning' });
          return;
        }
        const folderName = item?.data?.[0]?.folder_name || 'Downloaded_Files';
        const zip = new JSZip();
        enqueueSnackbar(`Preparing ${selectedImages.length} files...`, { variant: 'info' });
        for (const image of selectedImages) {
          const filename = image?.title || image?.src?.split('/')?.pop();
          const response = await fetch(image?.src);
          const blob = await response.blob();
          zip.file(filename, blob);
        }
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        saveAs(zipBlob, `${folderName}.zip`);
        enqueueSnackbar(`Downloaded ${selectedImages?.length} files as ${folderName}.zip`, {
          variant: 'success',
        });
        setSelectedImages([]);
      } else {
        const allImages = formattedImages || [];
        if (!allImages.length) {
          enqueueSnackbar("No files found to download.", { variant: "warning" });
          return;
        }
        const zip = new JSZip();
        const folderName = allImages?.data?.[0]?.folder_name || 'Downloaded_Files';
        enqueueSnackbar(`Preparing ${allImages.length} files for download...`, {
          variant: "info",
        });

        for (const image of allImages) {
          const filename = image?.title || image?.src?.split("/")?.pop();
          const response = await fetch(image?.src);
          const blob = await response.blob();
          zip.file(filename, blob);
        }

        const zipBlob = await zip.generateAsync({ type: "blob" });
        saveAs(zipBlob, `${folderName}_All.zip`);

        enqueueSnackbar(
          `Downloaded all ${allImages.length} files as ${folderName}_All.zip`,
          { variant: "success" }
        );
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Failed to download selected files.', { variant: 'error' });
    }
  };

  const totalSizeBytes = useMemo(
    () =>
      itemState?.data?.reduce((sum, it) => {
        const kb = Number(it?.size_kb) || 0;
        return sum + kb * 1024;
      }, 0),
    [itemState]
  );


  const fileTypes = useMemo(() => {
    const types = new Set(itemState?.data?.map((it) => it?.file_type).filter(Boolean));
    if (types.has('image') && types.has('video')) return 'Images & Videos';
    if (types.has('image')) return 'Images';
    if (types.has('video')) return 'Videos';
    if (types.size > 0) return [...types].join(', ');
    return 'Unknown';
  }, [itemState]);

  const isConfidential = useMemo(
    () =>
      (itemState?.data || []).some(
        (f) => f?.is_confidential === true || f?.is_confidential === 1 || f?.is_confidential === '1'
      ),
    [itemState]
  );

  // Combined ("View All Photos") view: group images by category or by project
  // name (groupBy === 'project'), preserving first-seen order.
  const categoryGroups = useMemo(() => {
    if (!combinedView) return [];
    const groups = [];
    const index = {};
    formattedImages.forEach((f) => {
      const key =
        (groupBy === 'project' ? f?.folder_name : f?.category_title) ||
        (groupBy === 'project' ? 'Untitled Project' : 'Uncategorized');
      if (index[key] === undefined) {
        index[key] = groups.length;
        groups.push({ title: key, images: [] });
      }
      groups[index[key]].images.push(f);
    });
    return groups;
  }, [combinedView, groupBy, formattedImages]);

  const renderCategory = (
    <>
      <Stack spacing={1.5}>
        <Typography variant="subtitle2" fontWeight={'bold'}>Category</Typography>

        {toggleTags.value && (
          <>
            <Typography variant="subtitle4" >
              {itemState?.data?.length && itemState?.data[0]?.category_title}
            </Typography>
          </>
        )}
      </Stack>
    </>
  );
  const renderCaption = (
    <>
      <Stack spacing={1.5}>
        <Typography variant="subtitle2" fontWeight={'bold'}>Caption</Typography>

        {toggleTags.value && (
          <>
            <Typography variant="subtitle4" >
              {itemState?.data?.length && itemState?.data[0]?.caption}
            </Typography>
          </>
        )}
      </Stack>
    </>
  );

  const renderProperties = (
    <Stack spacing={1.5}>
      <Stack sx={{ typography: 'subtitle2' }}> Properties </Stack>

      {properties.value && (
        <>
          <Stack direction="row" sx={{ typography: 'caption', textTransform: 'capitalize' }}>
            <Box component="span" sx={{ width: 80, color: 'text.secondary', mr: 2 }}>
              Size
            </Box>
            {fData(totalSizeBytes)}
          </Stack>

          <Stack direction="row" sx={{ typography: 'caption', textTransform: 'capitalize' }}>
            <Box component="span" sx={{ width: 80, color: 'text.secondary', mr: 2 }}>
              Modified
            </Box>
            {itemState?.data?.length && itemState?.data[0]?.folder_created_date}
          </Stack>

          <Stack direction="row" sx={{ typography: 'caption', textTransform: 'capitalize' }}>
            <Box component="span" sx={{ width: 80, color: 'text.secondary', mr: 2 }}>
              Type
            </Box>
            {fileFormat(fileTypes)}
          </Stack>
        </>
      )}
    </Stack>
  );

  const renderUploadedBy = (
    <>
    { itemState?.data?.length && itemState?.data[0]?.user_name && (
    <Stack spacing={1.5}>
      <Stack sx={{ typography: 'subtitle2' }}> Uploaded By </Stack>

    <Stack direction="row" sx={{ typography: 'caption', textTransform: 'capitalize' }}>
            <Box component="span" sx={{  color: 'text.secondary', mr: 2 }}>
             {itemState?.data[0]?.user_name}
            </Box>
          </Stack>

    </Stack>
    )}
    </>
  );

  return (
    <>
      <Drawer
        open={open}
        onClose={() => {
          setShowImageSelection(false);
          setSelectedImages([]);
          edit.onFalse();
          refetch();
          onClose();
          window.history.replaceState({}, '', window.location.origin + window.location.pathname);
        }}
        anchor="right"
        slotProps={{
          backdrop: { invisible: true },
        }}
        PaperProps={{
          sx: {
            width: '100%',
            minHeight: '100vh',
            bgcolor: 'background.neutral',
          },
        }}
        {...other}
      >
        {isLoading || loading.value ? (
          <LoadingScreen />
        ) : (
          <>
            {itemState?.data?.length > 0 ? (
              <FormProvider methods={methods} onSubmit={methods.handleSubmit(() => { })}>
                <Scrollbar sx={{ height: 1 }}>
                  <Stack
                    direction="row"
                    alignItems={{ md: 'center', xs: 'start' }}
                    justifyContent="space-between"
                    sx={{ p: 2.5 }}
                  >
                    <Box>
                    {title ? (
                      <Typography variant="h6">{title}</Typography>
                    ) : fromProject ? (
                      <>
                        <Typography variant="h6">
                          {itemState?.data?.length && itemState?.data[0]?.folder_name}
                        </Typography>
                      </>
                    ) : (
                      <>
                        {fromCategory ? (
                          <Typography variant="h6">
                            {itemState?.data?.length && itemState?.data[0]?.category_title}
                          </Typography>
                        ) : edit.value ? (
                          <LazyMotion features={domAnimation}>
                            <m.div
                              initial={{ opacity: 0, scale: 0.9, x: -20 }}
                              animate={{ opacity: 1, scale: 1, x: 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              <RHFTextField
                                name="folder"
                                defaultValue={
                                  (itemState?.data?.length && itemState?.data[0]?.folder_name) || ''
                                }
                                size="small"
                                autoFocus
                                onChange={(e) => {
                                  const newValue = e.target.value;
                                  methods.setValue('folder', newValue);
                                }}
                                sx={{
                                  [`& .${inputBaseClasses.input}`]: {
                                    typography: 'h6',
                                  },
                                }}
                              />
                            </m.div>
                          </LazyMotion>
                        ) : (
                          <Typography variant="h6">
                            {itemState?.data?.length && itemState?.data[0]?.folder_name}
                          </Typography>
                        )}
                      </>
                    )}
                    {isConfidential && (
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                        <Iconify icon="mdi:lock" width={16} sx={{ color: 'error.main' }} />
                        <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 600 }}>
                          Confidential
                        </Typography>
                      </Stack>
                    )}
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: { md: 'center', xs: 'start' },
                        gap: 1,
                        justifyContent: { md: 'center', xs: 'flex-end' },
                        flexDirection: { md: 'row', xs: 'column-reverse' },
                      }}
                    >
                      {!hideSelectButton && (showImageSelection ? (
                        <>
                          <Button variant="contained" color="primary" onClick={selectAll}>
                            {'Select All'}
                          </Button>
                          <Button variant="contained" color="primary" onClick={deSelect}>
                            {'Deselect'}
                          </Button>
                        </>
                      ) : (
                        <Button variant="contained" color="primary" onClick={select}>
                          {selectedImages?.length ? 'Deselect' : 'Select'}
                        </Button>
                      ))}
                      <Box sx={{ display: 'flex' }}>
                        {(user?.user_type === '2' || user?.user_type === '1') && (
                          <IconButton
                            onClick={(e) => {
                              popover.onOpen(e);
                            }}
                          >
                            <Iconify icon="eva:more-vertical-fill" />
                          </IconButton>
                        )}
                        {/* {itemState?.data?.length &&
                          // itemState?.data[0]?.file_status == '0' &&
                          (user?.user_type === '3' || user?.user_type === '4') && (
                            <IconButton
                              onClick={(e) => {
                                popover.onOpen(e);
                              }}
                            >
                              <Iconify icon="eva:more-vertical-fill" />
                            </IconButton>
                          )} */}
                        <IconButton
                          onClick={() => {
                            setSelectedImages([]);
                            setShowImageSelection(false);
                            edit.onFalse();
                            refetch();
                            onClose();
                            window.history.replaceState({}, '', window.location.origin + window.location.pathname);

                          }}
                        >
                          <Iconify icon="codex:cross" style={{ cursor: 'pointer' }} />
                        </IconButton>
                      </Box>
                      <CustomPopover
                        open={popover.open}
                        onClose={popover.onClose}
                        sx={{ width: 160, mt: 1 }}
                        arrow="top-right"
                      >
                        {(user?.user_type === '2' || user?.user_type === '1') && (
                          <>
                            {!combinedView && (
                            <MenuItem
                              onClick={() => {
                                edit.onToggle();
                                popover.onClose();
                              }}
                              sx={{ typography: 'body2' }}
                            >
                              <Iconify
                                icon="tabler:edit"
                                color="primary.main"
                                sx={{ mr: 2, width: 20, height: 20 }}
                              />
                              <span style={{ cursor: 'pointer' }}> Rename </span>
                            </MenuItem>
                            )}
                            <MenuItem
                              onClick={() => {
                                edit.onToggle();
                                popover.onClose();
                                handleDownloadSelected();
                              }}
                              sx={{ typography: 'body2' }}
                            >
                              <Iconify
                                icon="tabler:download"
                                color="primary.main"
                                sx={{ mr: 2, width: 20, height: 20 }}
                              />
                              <span style={{ cursor: 'pointer' }}> Download </span>
                            </MenuItem>
                            {!combinedView && (
                            <MenuItem
                              onClick={() => {
                                deleteFile.onTrue();
                                popover.onClose();
                              }}
                              sx={{ typography: 'body2' }}
                            >
                              <Iconify
                                icon="tabler:trash"
                                color="error.main"
                                sx={{ mr: 2, width: 20, height: 20 }}
                              />
                              <span style={{ cursor: 'pointer' }}> Delete </span>
                            </MenuItem>
                            )}
                          </>
                        )}

                        {itemState?.data?.length &&
                          // itemState?.data[0]?.file_status == '0' &&
                          (user?.user_type === '3' || user?.user_type === '4') && (
                            <>
                              {/* <MenuItem
                                onClick={() => {
                                  edit.onToggle();
                                  popover.onClose();
                                }}
                                sx={{ typography: 'body2' }}
                              >
                                <Iconify
                                  icon="tabler:edit"
                                  color="primary.main"
                                  sx={{ mr: 2, width: 20, height: 20 }}
                                />
                                <span style={{ cursor: 'pointer' }}> Rename </span>
                              </MenuItem> */}
                              <MenuItem
                                onClick={() => {
                                  edit.onToggle();
                                  popover.onClose();
                                  handleDownloadSelected();
                                }}
                                sx={{ typography: 'body2' }}
                              >
                                <Iconify
                                  icon="tabler:download"
                                  color="primary.main"
                                  sx={{ mr: 2, width: 20, height: 20 }}
                                />
                                <span style={{ cursor: 'pointer' }}> Download </span>
                              </MenuItem>
                              {/* <MenuItem
                                onClick={() => {
                                  deleteFile.onTrue();
                                  popover.onClose();
                                }}
                                sx={{ typography: 'body2' }}
                              >
                                <Iconify
                                  icon="tabler:trash"
                                  color="error.main"
                                  sx={{ mr: 2, width: 20, height: 20 }}
                                />
                                <span style={{ cursor: 'pointer' }}> Delete </span>
                              </MenuItem> */}
                            </>
                          )}
                      </CustomPopover>
                    </Box>
                  </Stack>

                  <Stack
                    spacing={2.5}
                    justifyContent="center"
                    sx={{
                      p: 2.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: { md: 3, xs: 1 },
                        flexWrap: 'wrap',
                      }}
                    >
                      {combinedView &&
                        categoryGroups.map((grp) => (
                          <Stack
                            key={grp.title}
                            spacing={1.5}
                            sx={{
                              width: '100%',
                              mb: 3,
                              border: 1,
                              borderColor: 'divider',
                              borderRadius: 1.5,
                              pb: 1,
                              pl: 1,
                            }}
                          >
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                mb: 1,
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                                color: 'text.primary',
                              }}
                            >
                              <Label
                                color="info"
                                sx={{ px: 2, py: 0.75, fontWeight: 600, borderRadius: '8px' }}
                              >
                                {grp.title}
                              </Label>
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                              {grp.images.map((slide, index) => (
                                <ImageCard
                                  key={index}
                                  slide={slide}
                                  lightbox={lightbox}
                                  fromCategory={fromCategory}
                                  category_title={slide?.category_title}
                                  folder_name={slide?.folder_name}
                                  caption={slide?.caption}
                                  handleSelectImage={handleSelectImage}
                                  selectedImages={selectedImages}
                                  showImageSelection={showImageSelection}
                                  is_confidential={slide?.is_confidential}
                                  user_name={slide?.user_name}
                                />
                              ))}
                            </Box>
                          </Stack>
                        ))}

                      {!combinedView && pendingImages?.length > 0 && (
                        <Stack spacing={1.5} sx={{
                          width: '100%', mb: 3,
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1.5,
                          pb: 1,
                          pl: 1
                        }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              mb: 1,
                              textTransform: 'uppercase',
                              letterSpacing: 0.5,
                              color: 'text.primary',

                            }}
                          >
                            <Label color="warning" sx={{
                              position: 'relative',
                              px: 2,
                              py: 0.75,
                              fontWeight: 600,
                              borderRadius: '8px',
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: 6,
                                borderRadius: '8px 0 0 8px',
                                bgcolor: 'warning.main',
                              },
                            }}
                            > Pending </Label>
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                            {pendingImages?.map((slide, index) => (
                              <ImageCard
                                key={index}
                                slide={slide}
                                lightbox={lightbox}
                                fromCategory={fromCategory}
                                category_title={slide?.category_title}
                                folder_name={slide?.folder_name}
                                caption={slide?.caption}
                                handleSelectImage={handleSelectImage}
                                selectedImages={selectedImages}
                                showImageSelection={showImageSelection}
                                is_confidential={slide?.is_confidential}
                              />
                            ))}
                          </Box>
                        </Stack>
                      )}

                      {!combinedView && acceptedImages?.length > 0 && (
                        <Stack spacing={1.5} sx={{
                          width: '100%', mb: 3,
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1.5,
                          pb: 1,
                          pl: 1
                        }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              mb: 1,
                              textTransform: 'uppercase',
                              letterSpacing: 0.5,
                              color: 'text.primary',
                            }}
                          >
                            <Label color="success" sx={{
                              position: 'relative',
                              px: 2,
                              py: 0.75,
                              fontWeight: 600,
                              borderRadius: '8px',
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: 6,
                                borderRadius: '8px 0 0 8px',
                                bgcolor: 'success.main',
                              },
                            }}> Accepted </Label>
                          </Typography>
                          <Box sx={{
                            display: 'flex', flexWrap: 'wrap', gap: 2
                          }}>
                            {acceptedImages?.map((slide, index) => (
                              <ImageCard
                                key={index}
                                slide={slide}
                                lightbox={lightbox}
                                fromCategory={fromCategory}
                                category_title={slide?.category_title}
                                folder_name={slide?.folder_name}
                                caption={slide?.caption}
                                handleSelectImage={handleSelectImage}
                                selectedImages={selectedImages}
                                showImageSelection={showImageSelection}
                                is_confidential={slide?.is_confidential}
                              />
                            ))}
                          </Box>
                        </Stack>
                      )}

                      {!combinedView &&
                        user?.user_type !== '3' &&
                        user?.user_type !== '4' &&
                        rejectedImages?.length > 0 && (
                          <Stack spacing={1.5} sx={{
                            width: '100%', mb: 3, border: 1,
                            borderColor: 'divider',
                            borderRadius: 1.5,
                             pb: 1,
                             pl: 1
                          }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                mb: 1,
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                                color: 'text.primary',
                              }}
                            >
                              <Label color="error" sx={{
                                position: 'relative',
                                px: 2,
                                py: 0.75,
                                fontWeight: 600,
                                borderRadius: '8px',
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  left: 0,
                                  top: 0,
                                  bottom: 0,
                                  width: 6,
                                  borderRadius: '8px 0 0 8px',
                                  bgcolor: 'error.main',
                                },
                              }}> Rejected </Label>
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                              {rejectedImages?.map((slide, index) => (
                                <ImageCard
                                  key={index}
                                  slide={slide}
                                  lightbox={lightbox}
                                  fromCategory={fromCategory}
                                  category_title={slide?.category_title}
                                  folder_name={slide?.folder_name}
                                  caption={slide?.caption}
                                  handleSelectImage={handleSelectImage}
                                  selectedImages={selectedImages}
                                  showImageSelection={showImageSelection}
                                  is_confidential={slide?.is_confidential}
                                />
                              ))}
                            </Box>
                          </Stack>
                        )}
                    </Box>
                    {!combinedView && <Divider sx={{ borderStyle: 'dashed' }} />}
                    {!fromCategory && !combinedView && renderCategory}
                    {!fromCategory &&
                      !combinedView &&
                      itemState?.data?.length &&
                      itemState?.data[0]?.caption &&
                      renderCaption}
                    {!fromCategory && !combinedView && renderProperties}
                    {!fromCategory && !combinedView && renderUploadedBy}
                  </Stack>
                </Scrollbar>
                <Box
                  sx={{
                    position: 'sticky',
                    bottom: 0,
                    p: 2.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                    zIndex: 10,
                    mb: { xs: 12, sm: 16, md: 10, lg: 45 },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      flexWrap: 'wrap',
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      flex: 1,
                      minWidth: 250,
                    }}
                  >
                    {!fromCategory && !combinedView && (user?.user_type === '2' || user?.user_type === '1') && (
                      <Button
                        variant="contained"
                        color="success"
                        sx={{ minWidth: '120px' }}
                        startIcon={<Iconify icon="material-symbols:check-circle-rounded" />}
                        onClick={confirmAccept.onTrue}
                      >
                        Accept {showImageSelection && `(${selectedImages?.length})`}
                      </Button>
                    )}

                    {!fromCategory && !combinedView && (user.user_type === '2' || user.user_type === '1') && (
                      <Button
                        variant="contained"
                        color="error"
                        sx={{ minWidth: '120px' }}
                        startIcon={<Iconify icon="material-symbols:cancel-rounded" />}
                        onClick={confirmReject.onTrue}
                      >
                        Reject
                      </Button>
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: { xs: 'center', md: 'flex-end' },
                      gap: 2,
                      flexWrap: 'wrap',
                      flex: 1,
                      minWidth: 250,
                    }}
                  >
                    {edit.value && (
                      <Button
                        variant="contained"
                        color="primary"
                        sx={{ minWidth: '120px' }}
                        startIcon={<Iconify icon="material-symbols:save" />}
                        onClick={handleRenameFolder}
                      >
                        Save Changes
                      </Button>
                    )}

                    {showImageSelection && (
                      <>
                        {!combinedView && itemState?.data?.length > 0 &&
                          !(
                            ['1', '2'].includes(itemState?.data[0]?.file_status) &&
                            ['3', '4'].includes(user?.user_type)
                          ) && (
                            <Button
                              variant="contained"
                              color="error"
                              sx={{ minWidth: '120px' }}
                              startIcon={<Iconify icon="flowbite:forward-solid" />}
                              onClick={() => {
                                if (selectedImages?.length > 0) {
                                  moveFile.onTrue();
                                } else {
                                  enqueueSnackbar('Please select at least one image to move.', {
                                    variant: 'warning',
                                  });
                                }
                              }}
                            >
                              Move ({selectedImages.length})
                            </Button>
                          )}

                        {showImageSelection && (
                          <Button
                            variant="contained"
                            color="error"
                            sx={{ minWidth: '120px' }}
                            startIcon={<Iconify icon="material-symbols:share" />}
                            onClick={() => {
                              if (selectedImages?.length > 0) {
                                share.onTrue();
                              } else {
                                enqueueSnackbar('Please select at least one image to share.', {
                                  variant: 'warning',
                                });
                              }
                            }}
                          >
                            Share ({selectedImages?.length})
                          </Button>
                        )}

                        <Button
                          variant="contained"
                          color="error"
                          sx={{ minWidth: '120px' }}
                          startIcon={<Iconify icon="material-symbols:download" />}
                          onClick={handleDownloadSelected}
                        >
                          Download ({selectedImages?.length})
                        </Button>
                      </>
                    )}
                  </Box>
                </Box>
              </FormProvider>
            ) : (
              <Box
                sx={{
                  height: '100vh',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  color: 'text.secondary',
                  p: 3,
                }}
              >
                <IconButton
                  onClick={() => {
                    setSelectedImages([]);
                    setShowImageSelection(false);
                    edit.onFalse();
                    onClose();
                  }}
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    bgcolor: 'background.paper',
                    boxShadow: 1,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Iconify icon="codex:cross" />
                </IconButton>
                {fromCategory && !fromProject && title && (
                  <Typography
                    variant="h6"
                    sx={{ position: 'absolute', top: 24, left: 20, color: 'text.primary' }}
                  >
                    {title}
                  </Typography>
                )}
                <Iconify
                  icon={fromCategory && !fromProject ? 'solar:gallery-wide-bold' : 'mdi:file-alert-outline'}
                  width={64}
                  height={64}
                  color="text.disabled"
                  sx={{ mb: 2 }}
                />
                {fromCategory && !fromProject ? (
                  <Typography variant="h6">No photos uploaded to this category</Typography>
                ) : (
                  <>
                    <Typography variant="h6">No data available</Typography>
                    <Typography variant="body2">
                      It seems this file or folder doesn’t contain any records.
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </>
        )}
      </Drawer>

      <Lightbox
        index={lightbox?.selected}
        slides={methods?.getValues('images')?.map((img) =>
          img?.type === 'video'
            ? {
              type: 'video',
              title: img?.title,
              width: 1280,
              height: 720,
              poster: img?.poster,
              sources: [
                {
                  src: img?.src,
                  type: 'video/mp4',
                },
              ],
            }
            : img
        )}
        open={lightbox.open}
        close={lightbox.onClose}
      />

      <FileManagerShareDialog
        selectedImages={selectedImages}
        totalImages={methods.getValues('images')}
        fromCategory={fromCategory}
        fromProject={fromProject}
        open={share.value}
        inviteEmail={inviteEmail}
        onChangeInvite={handleChangeInvite}
        onCopyLink={onCopyLink}
        setSelectedImages={setSelectedImages}
        onClose={() => {
          share.onFalse();
          setInviteEmail('');
        }}
      />
      <QuickMoveModal
        selectedImages={selectedImages}
        item={itemState}
        setItemState={setItemState}
        open={moveFile.value}
        onClose={moveFile.onFalse}
        setSelectedImages={setSelectedImages}
        setShowImageSelection={setShowImageSelection}
      />

      <ConfirmDialog
        title="Delete folder"
        content="Are you sure want to delete this folder?"
        open={deleteFile.value}
        onClose={deleteFile.onFalse}
        action={
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete
          </Button>
        }
      />

      <ConfirmDialog
        title="Confirm Accept"
        content={
          showImageSelection
            ? `Are you sure you want to accept the selected ${selectedImages?.length} image(s)?`
            : `Are you sure you want to accept all images in this folder?`
        }
        open={confirmAccept.value}
        onClose={confirmAccept.onFalse}
        action={
          <Button
            onClick={() => {
              confirmAccept.onFalse();
              handleAccept();
            }}
            variant="contained"
            color="success"
          >
            Confirm Accept
          </Button>
        }
      />

      <ConfirmDialog
        title="Confirm Reject"
        content={
          showImageSelection
            ? `Are you sure you want to reject the selected ${selectedImages?.length} image(s)?`
            : `Are you sure you want to reject images in this folder?`
        }
        open={confirmReject.value}
        onClose={confirmReject.onFalse}
        action={
          <Button
            onClick={() => {
              confirmReject.onFalse();
              handleReject();
            }}
            variant="contained"
            color="error"
          >
            Confirm Reject
          </Button>
        }
      />
    </>
  );
}

function ImageCard({
  index,
  slide,
  lightbox,
  category_title,
  folder_name,
  caption,
  handleSelectImage,
  selectedImages,
  fromCategory,
  showImageSelection,
  is_confidential,
  user_name,
}) {

  const isSelected = selectedImages?.some((img) => img?.id === slide?.id);

  const longPress = usePopover();

  const timerRef = useRef(null);

  const handleMouseDown = (e) => {
    // if (fromCategory) {
    //   const target = e.currentTarget;
    //   timerRef.current = setTimeout(() => {
    //     longPress.onOpen({ currentTarget: target });
    //   }, 1000);
    // }

    const target = e.currentTarget;
    timerRef.current = setTimeout(() => {
      longPress.onOpen({ currentTarget: target });
    }, 1000);
  };

  const handleMouseUp = () => {
    clearTimeout(timerRef.current);
  };

  const handleMouseLeave = () => {
    clearTimeout(timerRef.current);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        borderColor: 'grey.300',
        borderRadius: 2,
        p: 0.5,
        border: is_confidential ? '2px solid red' : '2px solid #e0e0e0',
        borderRadius: '12px',
        padding: '4px',
        backgroundColor: '#fafafa',
        transition: 'all 0.3s ease',
      }}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      key={index}
    >
      <Tooltip arrow placement="bottom" title={slide?.title}>
        <Box
          component={Image}
          src={slide?.type === 'video' ? slide?.poster : slide?.src}
          sx={{
            width: { md: 140, sm: 120, xs: 50 },
            height: { md: 140, sm: 120, xs: 50 },
            borderRadius: 1,
            cursor: 'pointer',
            backgroundColor: !slide?.poster ? 'black' : 'transparent',
            '&:hover': {
              opacity: 0.8,
              transform: 'scale(1.05)',
              transition: 'all 0.3s ease-in-out',
            },
          }}
          onClick={() => lightbox.onOpen(slide?.poster || slide?.src)}
        />
        <Box
          sx={{
            position: 'absolute',
            top: { md: 0, xs: -1 },
            right: { md: '10px', xs: '3px' },
            color: 'white',
            cursor: 'pointer',
            fontSize: { md: 20, xs: 10 },
            '&:hover': { color: 'primary.dark' },
          }}
          onClick={() => handleSelectImage(slide)}
        >
          {showImageSelection && (
            <IconButton>
              <Iconify
                icon={
                  // isSelected ? 'grommet-icons:radial-selected' : 'lsicon:radio-unselected-filled'
                  isSelected ? 'lets-icons:check-fill' : 'lsicon:radio-unselected-filled'
                }
                sx={{
                  width: { md: 24, xs: 16 },
                  height: { md: 24, xs: 16 },
                }}
                color={isSelected ? 'primary.main' : '#f1511b'}
              />
            </IconButton>
          )}
        </Box>
        {slide.type === 'video' && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: 'white',
              cursor: 'pointer',
              fontSize: { md: 40, xs: 30 },
            }}
            onClick={() => lightbox.onOpen(slide?.poster || slide?.src)}
          >
            <Iconify
              icon="eva:play-circle-fill"
              sx={{
                width: { md: 40, xs: 20 },
                height: { md: 40, xs: 20 },
              }}
            />
          </Box>
        )}
      </Tooltip>
      <CustomPopover
        open={longPress.open}
        onClose={longPress.onClose}
        sx={{ minWidth: { md: 200, xs: 100 }, p: 2, ml: 1 }}
        arrow="left-top"
      >
        {category_title && (
          <Stack spacing={1.5} direction={'row'} display={'flex'} alignItems={'center'}>
            <Typography variant="h6" fontWeight={'bold'} color={'primary'}>
              Category :
            </Typography>

            <Typography>{category_title}</Typography>
          </Stack>
        )}

        {folder_name && (
          <Stack spacing={1.5} direction={'row'} display={'flex'} alignItems={'center'}>
            <Typography variant="h6" fontWeight={'bold'} color={'primary'}>
              Folder :
            </Typography>

            <Typography> {folder_name} </Typography>
          </Stack>
        )}
        <Stack spacing={1.5} direction={'row'} display={'flex'} alignItems={'center'} flexWrap="wrap">
          <Typography variant="h6" fontWeight={'bold'} color={'primary'}>
            Location :
          </Typography>

          <Typography> {slide?.location ? slide.location : 'No Location Found'} </Typography>
        </Stack>

        {caption && (
          <Stack spacing={1.5} direction={'row'} display={'flex'} alignItems={'center'}>
            <Typography variant="h6" fontWeight={'bold'} color={'primary'}>
              Description :
            </Typography>

            <Typography> {caption} </Typography>
          </Stack>
        )}

        {user_name && (
          <Stack spacing={1.5} direction={'row'} display={'flex'} alignItems={'center'}>
            <Typography variant="h6" fontWeight={'bold'} color={'primary'}>
              Uploaded By :
            </Typography>

            <Typography> {user_name} </Typography>
          </Stack>
        )}
      </CustomPopover>
    </Box>
  );
}

FileManagerFileDetails.propTypes = {
  favorited: PropTypes.bool,
  item: PropTypes.any,
  onClose: PropTypes.func,
  onCopyLink: PropTypes.func,
  onFavorite: PropTypes.func,
  open: PropTypes.bool,
  isLoading: PropTypes.bool,
  refetch: PropTypes.func,
  fromCategory: PropTypes.bool,
  fromProject: PropTypes.bool,
  hideSelectButton: PropTypes.bool,
  combinedView: PropTypes.bool,
  groupBy: PropTypes.string,
  title: PropTypes.string,
};
